import { ArrowRight, TrendingUp, HeartPulse, LifeBuoy, Megaphone, ListChecks } from "lucide-react";
import { MANROPE, toneOf, LABEL } from "./overviewTokens";

const ICONS = { sales: TrendingUp, customer_success: HeartPulse, support: LifeBuoy, marketing: Megaphone, actions: ListChecks };

const initials = (name) => (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const MrrChart = ({ series, floor }) => {
  const max = Math.max(...series.map((s) => s.value), floor + 1);
  const span = Math.max(max - floor, 1);
  return (
    <div className="flex items-end gap-2 h-[150px]" data-testid="overview-mrr-chart">
      {series.map((s, i) => {
        const h = Math.max(6, Math.round(((s.value - floor) / span) * 132));
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5" title={`${s.label}`}>
            <div className="w-full rounded-[2px]" style={{ height: h, background: s.current ? "#FFD108" : "#E6E1D6" }} />
            <div className="text-[10px] text-[#8A857C]">{s.month}</div>
          </div>
        );
      })}
    </div>
  );
};

const RevenueBand = ({ rb, onOpen }) => (
  <div className="bg-white border border-[#DAD5CA] rounded-[10px] p-5" data-testid="overview-revenue-band"
       style={{ display: "grid", gap: 24, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,380px), 1fr))" }}>
    <div className="min-w-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={LABEL}>Revenue · MRR</span>
        <span className="text-[11.5px] text-[#8A857C]">Owner <span className="text-[#1A1A1A] font-semibold">{rb.owner}</span></span>
      </div>
      <div className="mt-2 flex items-baseline gap-3 flex-wrap">
        <span className="text-[52px] font-extrabold leading-none tracking-[-0.02em] whitespace-nowrap" style={MANROPE}>{rb.mrr}</span>
        <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: rb.change_tone === "green" ? "#1E6B3A" : "#D24317" }}>{rb.change}</span>
      </div>
      <div className="mt-5 pt-4 border-t border-[#EFECE4] grid grid-cols-2 sm:grid-cols-4 gap-4">
        {rb.figures.map((f) => (
          <div key={f.label} className="min-w-0">
            <div className="text-[11.5px] text-[#8A857C] leading-[1.3]">{f.label}</div>
            <div className="mono text-[17px] font-bold mt-1 whitespace-nowrap" style={{ color: toneOf(f.tone).ink }}>{f.value}</div>
          </div>
        ))}
      </div>
    </div>
    <div className="min-w-0 flex flex-col justify-end">
      <MrrChart series={rb.series} floor={rb.floor} />
      <button onClick={onOpen} data-testid="overview-open-revenue"
              className="mt-3 self-end inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#8A6D00] hover:text-[#6B5600] hover:underline">
        Open Revenue <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  </div>
);

const AreaCard = ({ a, onOpen, wide }) => {
  const tone = toneOf(a.tone);
  const Icon = ICONS[a.key] || ListChecks;
  return (
    <div className="bg-white border border-[#DAD5CA] rounded-[10px] p-4 flex flex-col"
         style={{ minHeight: 300, gridColumn: wide ? "1 / -1" : undefined }}
         data-testid={`overview-card-${a.key}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: "#8A857C" }} />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#59554D] truncate">{a.name}</span>
        <div className="ml-auto flex items-center gap-1.5 min-w-0">
          <span className="w-5 h-5 rounded-full bg-[#F1EEE6] text-[9px] font-bold text-[#59554D] flex items-center justify-center shrink-0">{initials(a.owner)}</span>
          <span className="text-[11.5px] text-[#59554D] truncate">{a.owner}</span>
        </div>
      </div>

      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
        <span className="text-[40px] font-extrabold leading-none whitespace-nowrap" style={{ ...MANROPE, color: a.error ? "#8A857C" : tone.ink }}>
          {a.error || a.headline.value}
        </span>
        <span className="text-[12.5px] text-[#59554D]">{a.error ? "Try again shortly" : a.headline.label}</span>
      </div>

      <div className="mt-4">
        {a.measures.map((m, i) => (
          <div key={i} className={`flex items-center justify-between gap-3 py-2 ${i ? "border-t border-[#EFECE4]" : ""}`}>
            <span className="text-[12.5px] text-[#59554D] min-w-0">{m.label}</span>
            <span className="mono text-[13px] font-bold whitespace-nowrap" style={{ color: toneOf(m.tone).ink }}>{m.value}</span>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-4">
        <div className={LABEL}>{a.watch.label}</div>
        <div className="mt-1.5 space-y-1">
          {a.watch.items.length === 0
            ? <div className="text-[12px] text-[#B5AFA3]">Nothing needs attention</div>
            : a.watch.items.map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3">
                <span className="text-[12.5px] text-[#1A1A1A] font-semibold min-w-0 leading-[1.35]">{it.name}</span>
                <span className="text-[11.5px] text-[#8A857C] whitespace-nowrap shrink-0">{it.owner}</span>
              </div>
            ))}
        </div>
        <button onClick={() => onOpen(a)} data-testid={`overview-open-${a.key}`}
                className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#8A6D00] hover:text-[#6B5600] hover:underline">
          {a.link_label} <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export const ScorecardView = ({ data, onOpen }) => {
  const revenue = data.areas.find((a) => a.key === "revenue");
  const rest = data.areas.filter((a) => a.key !== "revenue");
  return (
    <div className="space-y-4" data-testid="overview-scorecard">
      <RevenueBand rb={data.revenue_band} onOpen={() => onOpen(revenue)} />
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
        {rest.map((a) => <AreaCard key={a.key} a={a} onOpen={onOpen} wide={a.key === "actions"} />)}
      </div>
    </div>
  );
};
